import { Core } from "@faire/design-tokens";
import { IDiscoverPageHeroBanner } from "@faire/web-api/indigofair/data/IDiscoverPageHeroBanner";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { localNum } from "@faire/web/common/localization/localize";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web/common/media";
import { NonBreaking } from "@faire/web/common/string";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import capitalize from "lodash/capitalize";
import React, { use } from "react";

import { DiscoverPageHeroBannerData } from "@faire/retailer/app/(app)/visitor/[locale]/(pagesWithModal)/(defaultHeader)/discover/fetchHeroBannerData";
import { ShimmerPlaceHolder } from "@faire/retailer/components/Animation/ShimmerPlaceHolder";
import { SignUpElement, SignUpType } from "@faire/retailer/lib/popSignUp";

import * as styles from "./LoggedOutDiscoverPageHeroBannerPhase3.css";
import { SignUpButton } from "./SignUpButton";

const DEFAULT_BACKGROUND_COLOR = "#083114";

const DiscoverPageHeroTextPhase3: React.FC<{
  searchTerm: string;
  heroBannerData: IDiscoverPageHeroBanner;
  totalBrandCount: number;
  isSettingSemSimplifiedLpEnabled: boolean;
}> = ({
  searchTerm,
  heroBannerData,
  totalBrandCount,
  isSettingSemSimplifiedLpEnabled,
}) => {
  const { text_color, title, subtitle } = heroBannerData;
  const productSearchString = capitalize(searchTerm);
  const textColorOrDefault: string = text_color ?? Core.text.inverse;

  const renderTitle = () => {
    if (title) {
      return title;
    }

    return (
      <StrictLocalMsg
        defaultMessage="Wholesale {queryProductName} for your{nbsp}store"
        values={{
          queryProductName: productSearchString,
          nbsp: NonBreaking.space,
        }}
        description={{
          text: "header for discover page explaining you can buy the item at wholesale prices",
          img: "https://cdn.faire.com/fastly/7ab0bf0cdb8db14e66933ffe35b46db73faa9f353d04a80d140b134967137191.png",
        }}
      />
    );
  };

  const renderSubtitle = () => {
    if (isSettingSemSimplifiedLpEnabled) {
      return (
        <StrictLocalMsg
          defaultMessage="Buy from {totalBrandCountNumber}+ brands, all from one place."
          values={{ totalBrandCountNumber: localNum(totalBrandCount) }}
          description={{
            text: "subheading for discover page, encouraging retailers to browse the site",
          }}
        />
      );
    }

    if (subtitle) {
      return subtitle;
    }

    return (
      <StrictLocalMsg
        defaultMessage="From frames to decorative objects and everything in between, whatever you're looking for, find it on{nbsp}Faire."
        values={{ nbsp: NonBreaking.space }}
        description={{
          text: "subheading for discover page, encouraging retailers to browse the site",
          img: "https://cdn.faire.com/fastly/7ab0bf0cdb8db14e66933ffe35b46db73faa9f353d04a80d140",
        }}
      />
    );
  };

  return (
    <div className="flex flex-col items-start break-words w-full mobile:gap-1 tablet:gap-4 xxlarge:w-[513px]">
      <Typography
        variant={{
          mobileAndAbove: "pageHeaderSerifRegular",
          tabletAndAbove: "displaySSerifRegular",
          xLargeAndAbove: "displayMSerifRegular",
        }}
        as="h1"
        color={textColorOrDefault}
      >
        {renderTitle()}
      </Typography>
      <Typography
        variant={{
          mobileAndAbove: "paragraphSansRegular",
          xLargeAndAbove: "subheadingSansRegular",
        }}
        color={textColorOrDefault}
      >
        {renderSubtitle()}
      </Typography>
    </div>
  );
};

export const DiscoverPageImageryBannerPhase3: React.FC<
  DiscoverPageHeroBannerData
> = (props) => {
  const { searchTerm } = props;

  const heroBannerData = use(props.heroBannerDataPromise);
  const isSettingSemSimplifiedLpEnabled = use(
    props.isSettingSemSimplifiedLpEnabledPromise
  );
  const totalBrandCount = use(props.totalBrandCountPromise);

  const pictureSizes = [
    `(min-width: ${MIN_WIDTH_BREAKPOINTS.XXLARGE}px) 1200px`,
    `(min-width: ${MIN_WIDTH_BREAKPOINTS.DESKTOP}px) 800px`,
    `(min-width: ${MIN_WIDTH_BREAKPOINTS.TABLET}px) 700px`,
    `454px`,
  ].join(", ");

  const image =
    heroBannerData?.background_image ??
    IImage.build({
      url: "https://cdn.faire.com/fastly/a7468f8f0ee3aad7493bd75b1b09916462094747d1e88490d8bed10cc9b84d51.png",
    });
  const fallbackSrc = optimizeImageUrl(image, true, {
    width: 1200,
  });
  const srcSet = [2400, 1600, 1200, 800, 700, 454]
    .map((width) => {
      const url = optimizeImageUrl(image, true, { width });
      return `${url} ${width}w`;
    })
    .join(", ");

  return (
    <div
      className={styles.wrapper}
      style={{
        backgroundColor:
          heroBannerData?.background_color ?? DEFAULT_BACKGROUND_COLOR,
      }}
    >
      <div className={styles.bannerTextWrapper}>
        <DiscoverPageHeroTextPhase3
          heroBannerData={heroBannerData}
          searchTerm={searchTerm}
          totalBrandCount={totalBrandCount}
          isSettingSemSimplifiedLpEnabled={isSettingSemSimplifiedLpEnabled}
        />
        <SignUpButton
          type={SignUpType.GENERIC}
          element={SignUpElement.TOP_BANNER}
          sourceId="logged_out_discover_redesign_p3_banner_experiment"
          variant="secondary"
          style={{ border: "none" }}
        >
          <StrictLocalMsg
            defaultMessage="Sign up for free"
            description={{
              text: "sign up cta for discover page header",
              img: "https://cdn.faire.com/fastly/d21596fe32412c4ed5e66b8126825c2cec443186715ab55c7eda5f5488718062.png",
            }}
          />
        </SignUpButton>
      </div>
      <picture className={styles.bannerImageWrapper}>
        <source srcSet={srcSet} sizes={pictureSizes} />
        <img
          className={styles.bannerImage}
          src={fallbackSrc}
          data-test-id="banner-image"
          alt={heroBannerData?.background_image?.alt_text ?? searchTerm}
        />
      </picture>
    </div>
  );
};

export const VerticalizedBannerLoadingSkeleton: React.FC = () => {
  return (
    <div className={styles.loadingWrapper}>
      <div className={styles.bannerTextWrapper}>
        <ShimmerPlaceHolder
          className={styles.shimmerTitle}
          $backgroundColor={Core.surface.inverse}
        />
        <ShimmerPlaceHolder
          className={styles.shimmerSubtitle}
          $backgroundColor={Core.surface.inverse}
        />
        <ShimmerPlaceHolder
          $height={48}
          $width={150}
          $backgroundColor={Core.surface.inverse}
        />
      </div>
      <div className={styles.loadingImgSkeleton} />
    </div>
  );
};
